import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import { useLanguage } from '../i18n/LanguageContext';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const CHART_COLORS = ['#16a34a', '#ef4444', '#f59e0b', '#3b82f6', '#8b5cf6', '#ec4899'];

const WALLPAPERS = [
  { id: 'none', label: 'Default', preview: '#f0f4f8' },
  { id: 'gradient-ocean', label: 'Ocean', preview: 'linear-gradient(135deg, #0c3547 0%, #1a6b8a 100%)' },
  { id: 'gradient-sunset', label: 'Sunset', preview: 'linear-gradient(135deg, #2c1654 0%, #c94b4b 100%)' },
  { id: 'gradient-forest', label: 'Forest', preview: 'linear-gradient(135deg, #134e5e 0%, #71b280 100%)' },
  { id: 'gradient-night', label: 'Night Sky', preview: 'linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)' },
  { id: 'gradient-warm', label: 'Warm', preview: 'linear-gradient(135deg, #f5af19 0%, #f12711 100%)' },
  { id: 'gradient-slate', label: 'Slate', preview: 'linear-gradient(135deg, #2c3e50 0%, #4ca1af 100%)' },
];

const THEME_COLORS = [
  { id: '#2c5364', label: 'Default Teal' },
  { id: '#1e3a5f', label: 'Navy' },
  { id: '#4a1942', label: 'Plum' },
  { id: '#1b4332', label: 'Emerald' },
  { id: '#7c2d12', label: 'Amber' },
  { id: '#581c87', label: 'Purple' },
  { id: '#1e293b', label: 'Slate' },
  { id: '#991b1b', label: 'Crimson' },
];

const AdminDashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [checkinTrends, setCheckinTrends] = useState([]);
  const [regTrends, setRegTrends] = useState([]);
  const [vitalsSummary, setVitalsSummary] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [newLog, setNewLog] = useState({ content: '', note_type: 'log', target_user_id: '' });
  const [prefs, setPrefs] = useState({ theme_color: '#2c5364', wallpaper: 'none', widgets_order: ['stats', 'charts', 'checkins'] });
  const [resetPwUserId, setResetPwUserId] = useState(null);
  const [resetPwValue, setResetPwValue] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, usersRes, alertsRes, trendsRes, regRes, vitalsRes, logsRes, prefsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/alerts`),
        axios.get(`${API}/admin/analytics/checkin-trends`),
        axios.get(`${API}/admin/analytics/registration-trends`),
        axios.get(`${API}/admin/analytics/vitals-summary`),
        axios.get(`${API}/admin/logs`),
        axios.get(`${API}/admin/preferences`)
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setAlerts(alertsRes.data);
      setCheckinTrends(trendsRes.data);
      setRegTrends(regRes.data);
      setVitalsSummary(vitalsRes.data);
      setLogs(logsRes.data);
      setPrefs(prefsRes.data);
    } catch (err) {
      if (err?.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fetchUserDetail = async (userId) => {
    try {
      const res = await axios.get(`${API}/admin/users/${userId}`);
      setUserDetail(res.data);
      setSelectedUser(userId);
    } catch { toast.error('Failed to load user details'); }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Delete ${userName} and ALL their data? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success(`User ${userName} deleted`);
      setSelectedUser(null);
      setUserDetail(null);
      fetchData();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to delete user'); }
  };

  const handleResetPassword = async (userId) => {
    if (!resetPwValue || resetPwValue.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      await axios.put(`${API}/admin/users/${userId}/reset-password`, { new_password: resetPwValue });
      toast.success('Password reset successfully');
      setResetPwUserId(null);
      setResetPwValue('');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to reset password'); }
  };

  const handleCreateLog = async (e) => {
    e.preventDefault();
    if (!newLog.content.trim()) return;
    try {
      await axios.post(`${API}/admin/logs`, newLog);
      toast.success('Log entry created');
      setNewLog({ content: '', note_type: 'log', target_user_id: '' });
      const res = await axios.get(`${API}/admin/logs`);
      setLogs(res.data);
    } catch { toast.error('Failed to create log'); }
  };

  const handleDeleteLog = async (logId) => {
    try {
      await axios.delete(`${API}/admin/logs/${logId}`);
      setLogs(prev => prev.filter(l => l.id !== logId));
    } catch { toast.error('Failed to delete log'); }
  };

  const savePrefs = async (updates) => {
    const newPrefs = { ...prefs, ...updates };
    setPrefs(newPrefs);
    try {
      await axios.put(`${API}/admin/preferences`, newPrefs);
      toast.success('Preferences saved');
    } catch { toast.error('Failed to save preferences'); }
  };

  const formatDate = (iso) => {
    if (!iso) return 'N/A';
    return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  const formatShortDate = (d) => {
    const parts = d.split('-');
    return `${parts[1]}/${parts[2]}`;
  };

  const filteredUsers = users.filter(u =>
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const pieData = stats ? [
    { name: 'OK', value: stats.ok_users },
    { name: 'Overdue', value: stats.overdue_users }
  ] : [];

  if (loading) return <div className="loading-screen">Loading admin dashboard...</div>;

  const adminLinks = [
    { to: '/admin', label: 'Admin', icon: 'fa-shield-halved' },
    { to: '/admin?tab=users', label: 'Users', icon: 'fa-users', onClick: () => setActiveTab('users') },
    { to: '/dashboard', label: 'Dashboard', icon: 'fa-house' },
    { to: '/calendar', label: 'Calendar', icon: 'fa-calendar-days' },
    { to: '/profile', label: 'Profile', icon: 'fa-user' },
    { to: '/documents', label: 'Documents', icon: 'fa-file-lines' },
    { to: '/vitals', label: 'Vitals', icon: 'fa-heart-pulse' },
    { to: '/clients', label: 'Clients', icon: 'fa-user-group' },
    { to: '/directory', label: 'Directory', icon: 'fa-people-group' },
    { to: '/news', label: 'News', icon: 'fa-newspaper' },
    { to: '/help', label: 'Help', icon: 'fa-circle-question' }
  ];

  const wallpaperStyle = prefs.wallpaper !== 'none' 
    ? { background: WALLPAPERS.find(w => w.id === prefs.wallpaper)?.preview || '#f0f4f8', minHeight: '100vh' }
    : {};

  const themeColor = prefs.theme_color || '#2c5364';

  return (
    <div className="dashboard-container" data-testid="admin-dashboard" style={wallpaperStyle}>      <nav className="dashboard-nav" data-testid="admin-nav">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Link to="/admin">
            <img src="/careguard-logo.png" alt="CareGuard" className="nav-logo" style={{ cursor: 'pointer' }} />
          </Link>
          <span style={{ background: '#ef4444', color: '#fff', padding: '3px 10px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: '700', letterSpacing: '0.5px' }}>ADMIN</span>
          <LanguageSwitcher />
        </div>
        <div className="nav-links">
          {adminLinks.map(link => (
            link.onClick ? (
              <a key={link.to} href="#" onClick={(e) => { e.preventDefault(); link.onClick(); }}
                className={`nav-link ${activeTab === 'users' && link.label === 'Users' ? 'active' : ''}`}
                data-testid={`admin-nav-${link.label.toLowerCase()}`}>
                <i className={`fa-solid ${link.icon}`} style={{ marginRight: '3px', fontSize: '0.7rem' }}></i> {link.label}
              </a>
            ) : (
              <Link key={link.to} to={link.to} className={`nav-link ${window.location.pathname === link.to && link.label !== 'Users' ? 'active' : ''}`} data-testid={`admin-nav-${link.label.toLowerCase()}`}>
                <i className={`fa-solid ${link.icon}`} style={{ marginRight: '3px', fontSize: '0.7rem' }}></i> {link.label}
              </Link>
            )
          ))}
          <button onClick={onLogout} className="logout-button" data-testid="admin-logout-btn">{t('common.logout')}</button>
        </div>
      </nav>

      <div className="dashboard-content page-container">
        <h1 className="page-title" data-testid="admin-title">
          <i className="fa-solid fa-shield-halved" style={{ color: '#2c5364', marginRight: '10px' }}></i>Admin Dashboard
        </h1>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
          {[
            { key: 'overview', label: 'Overview', icon: 'fa-chart-pie' },
            { key: 'charts', label: 'Analytics', icon: 'fa-chart-line' },
            { key: 'users', label: 'Users', icon: 'fa-users' },
            { key: 'alerts', label: 'Alerts', icon: 'fa-triangle-exclamation', badge: alerts.length },
            { key: 'logs', label: 'Logs', icon: 'fa-clipboard-list' },
            { key: 'settings', label: 'Customize', icon: 'fa-palette' }
          ].map(tab => (
            <button key={tab.key} onClick={() => { setActiveTab(tab.key); setSelectedUser(null); }}
              data-testid={`admin-tab-${tab.key}`}
              style={{
                padding: '0.5rem 1.25rem', borderRadius: '8px', border: 'none', cursor: 'pointer',
                fontWeight: activeTab === tab.key ? '700' : '500', fontSize: '0.95rem',
                background: activeTab === tab.key ? '#2c5364' : '#e2e8f0',
                color: activeTab === tab.key ? '#fff' : '#4a6572', transition: 'all 0.2s'
              }}>
              <i className={`fa-solid ${tab.icon}`} style={{ marginRight: '6px' }}></i>{tab.label}
              {tab.badge > 0 && (
                <span style={{ marginLeft: '6px', background: '#ef4444', color: '#fff', borderRadius: '50%', width: '20px', height: '20px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem' }}>{tab.badge}</span>
              )}
            </button>
          ))}
        </div>

        {/* ===== OVERVIEW TAB ===== */}
        {activeTab === 'overview' && stats && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
              {[
                { label: 'Total Users', value: stats.total_users, icon: 'fa-users', color: '#3b82f6', action: () => setActiveTab('users') },
                { label: 'OK Status', value: stats.ok_users, icon: 'fa-circle-check', color: '#16a34a', action: () => setActiveTab('users') },
                { label: 'Overdue', value: stats.overdue_users, icon: 'fa-clock', color: '#ef4444', action: () => setActiveTab('alerts') },
                { label: 'Total Clients', value: stats.total_clients, icon: 'fa-user-group', color: '#8b5cf6', action: () => navigate('/clients') },
                { label: 'Check-ins', value: stats.total_checkins, icon: 'fa-clipboard-check', color: '#f59e0b', action: () => setActiveTab('charts') },
                { label: 'Events', value: stats.total_events, icon: 'fa-calendar', color: '#06b6d4', action: () => navigate('/calendar') },
                { label: 'Legal Docs', value: stats.total_documents, icon: 'fa-file-lines', color: '#ec4899', action: () => navigate('/documents') },
                { label: 'Contacts', value: stats.total_contacts, icon: 'fa-phone', color: '#14b8a6', action: () => navigate('/profile') }
              ].map(stat => (
                <button
                  key={stat.label}
                  onClick={stat.action}
                  className="section-card admin-stat-card"
                  style={{ textAlign: 'center', padding: '1.25rem', cursor: 'pointer', border: 'none', background: 'white', width: '100%' }}
                  data-testid={`stat-${stat.label.toLowerCase().replace(/\s/g, '-')}`}
                  title={`Click to view ${stat.label}`}
                >
                  <div style={{ fontSize: '1.5rem', color: stat.color, marginBottom: '0.5rem' }}><i className={`fa-solid ${stat.icon}`}></i></div>
                  <div style={{ fontSize: '2rem', fontWeight: '700', color: '#2c5364' }}>{stat.value}</div>
                  <div style={{ fontSize: '0.8rem', color: '#6b8899' }}>{stat.label}</div>
                </button>
              ))}
            </div>

            {/* Quick Charts Row */}
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1.5rem', marginBottom: '2rem' }}>
              <div className="section-card">
                <h3 style={{ color: '#2c5364', marginBottom: '1rem' }}><i className="fa-solid fa-chart-line" style={{ marginRight: '8px' }}></i>Check-in Trends (30 Days)</h3>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={checkinTrends}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" tickFormatter={formatShortDate} tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip labelFormatter={d => new Date(d).toLocaleDateString()} />
                    <Line type="monotone" dataKey="count" stroke="#2c5364" strokeWidth={2} dot={false} name="Check-ins" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="section-card" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <h3 style={{ color: '#2c5364', marginBottom: '0.5rem' }}><i className="fa-solid fa-chart-pie" style={{ marginRight: '8px' }}></i>User Status</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={4} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                      {pieData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Recent Check-ins */}
            <div className="section-card">
              <h3 style={{ color: '#2c5364', marginBottom: '1rem' }}><i className="fa-solid fa-clock-rotate-left" style={{ marginRight: '8px' }}></i>Recent Check-ins</h3>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e2e8f0', textAlign: 'left' }}>
                      <th style={{ padding: '0.6rem', color: '#6b8899' }}>User</th>
                      <th style={{ padding: '0.6rem', color: '#6b8899' }}>Time</th>
                      <th style={{ padding: '0.6rem', color: '#6b8899' }}>Status</th>
                      <th style={{ padding: '0.6rem', color: '#6b8899' }}>Feeling</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.recent_checkins?.map((c, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '0.6rem' }}><strong style={{ color: '#2c5364' }}>{c.user_name}</strong><br /><span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{c.user_email}</span></td>
                        <td style={{ padding: '0.6rem', color: '#4a6572' }}>{formatDate(c.timestamp)}</td>
                        <td style={{ padding: '0.6rem' }}><span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '0.8rem', background: '#dcfce7', color: '#16a34a', fontWeight: '600' }}>{c.status || 'ok'}</span></td>
                        <td style={{ padding: '0.6rem', color: '#4a6572' }}>{c.feeling || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ===== ANALYTICS/CHARTS TAB ===== */}
        {activeTab === 'charts' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
              {/* Check-in Trends */}
              <div className="section-card" data-testid="chart-checkin-trends">
                <h3 style={{ color: '#2c5364', marginBottom: '1rem' }}><i className="fa-solid fa-chart-line" style={{ marginRight: '8px' }}></i>Daily Check-ins (30 Days)</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={checkinTrends}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" tickFormatter={formatShortDate} tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip labelFormatter={d => new Date(d).toLocaleDateString()} />
                    <Bar dataKey="count" fill="#2c5364" radius={[3, 3, 0, 0]} name="Check-ins" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Registration Trends */}
              <div className="section-card" data-testid="chart-registration-trends">
                <h3 style={{ color: '#2c5364', marginBottom: '1rem' }}><i className="fa-solid fa-user-plus" style={{ marginRight: '8px' }}></i>New Registrations (30 Days)</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={regTrends}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" tickFormatter={formatShortDate} tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip labelFormatter={d => new Date(d).toLocaleDateString()} />
                    <Bar dataKey="count" fill="#8b5cf6" radius={[3, 3, 0, 0]} name="Registrations" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
              {/* Status Pie */}
              <div className="section-card" data-testid="chart-status-pie">
                <h3 style={{ color: '#2c5364', marginBottom: '1rem' }}><i className="fa-solid fa-chart-pie" style={{ marginRight: '8px' }}></i>User Status Breakdown</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={4} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                      {pieData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i]} />)}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Vitals Summary */}
              <div className="section-card" data-testid="chart-vitals-summary">
                <h3 style={{ color: '#2c5364', marginBottom: '1rem' }}><i className="fa-solid fa-heart-pulse" style={{ marginRight: '8px' }}></i>Average Vitals (30 Days)</h3>
                {vitalsSummary?.total_entries === 0 ? (
                  <p style={{ color: '#94a3b8', textAlign: 'center', padding: '2rem' }}>No vitals data available</p>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem' }}>
                    {[
                      { label: 'Systolic BP', key: 'blood_pressure_systolic', unit: 'mmHg', icon: 'fa-droplet', color: '#ef4444' },
                      { label: 'Diastolic BP', key: 'blood_pressure_diastolic', unit: 'mmHg', icon: 'fa-droplet', color: '#f97316' },
                      { label: 'Heart Rate', key: 'heart_rate', unit: 'bpm', icon: 'fa-heart-pulse', color: '#ec4899' },
                      { label: 'Temperature', key: 'temperature', unit: 'F', icon: 'fa-temperature-half', color: '#f59e0b' },
                      { label: 'O2 Sat', key: 'oxygen_saturation', unit: '%', icon: 'fa-lungs', color: '#3b82f6' },
                      { label: 'Weight', key: 'weight', unit: 'lbs', icon: 'fa-weight-scale', color: '#8b5cf6' }
                    ].map(v => (
                      <div key={v.key} style={{ padding: '0.75rem', borderRadius: '8px', background: '#f8f9fa', textAlign: 'center' }}>
                        <div style={{ color: v.color, marginBottom: '0.25rem' }}><i className={`fa-solid ${v.icon}`}></i></div>
                        <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#2c5364' }}>
                          {vitalsSummary?.averages?.[v.key] ?? '-'}
                        </div>
                        <div style={{ fontSize: '0.7rem', color: '#6b8899' }}>{v.label} ({v.unit})</div>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ marginTop: '0.75rem', fontSize: '0.8rem', color: '#94a3b8', textAlign: 'center' }}>
                  Based on {vitalsSummary?.total_entries || 0} entries
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ===== USERS TAB ===== */}
        {activeTab === 'users' && (
          <div style={{ display: 'grid', gridTemplateColumns: selectedUser ? '1fr 1fr' : '1fr', gap: '1.5rem' }}>
            <div>
              <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search users by name or email..."
                style={{ width: '100%', padding: '0.75rem 1rem', borderRadius: '8px', border: '2px solid #e2e8f0', fontSize: '0.95rem', marginBottom: '1rem' }} data-testid="admin-user-search" />
              <div className="section-card" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                <h3 style={{ color: '#2c5364', marginBottom: '1rem' }}>All Users ({filteredUsers.length})</h3>
                {filteredUsers.map(u => {
                  const isOverdue = u.next_checkin_due && new Date() > new Date(u.next_checkin_due);
                  return (
                    <div key={u.id} onClick={() => fetchUserDetail(u.id)} data-testid={`admin-user-${u.id}`}
                      style={{ padding: '0.75rem', borderRadius: '8px', cursor: 'pointer', borderLeft: `4px solid ${isOverdue ? '#ef4444' : '#16a34a'}`, background: selectedUser === u.id ? '#e7f3ff' : '#f8f9fa', marginBottom: '0.5rem', transition: 'all 0.15s' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div><strong style={{ color: '#2c5364' }}>{u.name}</strong><br /><span style={{ fontSize: '0.8rem', color: '#6b8899' }}>{u.email}</span></div>
                        <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '600', background: isOverdue ? '#fee2e2' : '#dcfce7', color: isOverdue ? '#dc2626' : '#16a34a' }}>{isOverdue ? 'Overdue' : 'OK'}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            {selectedUser && userDetail && (
              <div className="section-card" data-testid="admin-user-detail">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                  <h3 style={{ color: '#2c5364', margin: 0 }}>{userDetail.user.name}</h3>
                  <button onClick={() => handleDeleteUser(userDetail.user.id, userDetail.user.name)}
                    style={{ background: '#fee2e2', border: 'none', borderRadius: '6px', padding: '0.4rem 0.75rem', color: '#dc2626', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '600' }} data-testid="admin-delete-user-btn">
                    <i className="fa-solid fa-trash"></i> Delete
                  </button>
                </div>
                <div style={{ fontSize: '0.9rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <div><strong>Email:</strong> {userDetail.user.email}</div>
                  <div><strong>Phone:</strong> {userDetail.user.phone || 'N/A'}</div>
                  <div><strong>Location:</strong> {[userDetail.user.county, userDetail.user.state].filter(Boolean).join(', ') || 'N/A'}</div>
                  <div><strong>Last Check-in:</strong> {formatDate(userDetail.user.last_checkin)}</div>
                  <div><strong>Next Due:</strong> {formatDate(userDetail.user.next_checkin_due)}</div>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                    {[
                      { icon: 'fa-user-group', count: userDetail.clients?.length || 0, label: 'Clients', bg: '#e7f3ff', color: '#2c5364' },
                      { icon: 'fa-phone', count: userDetail.emergency_contacts?.length || 0, label: 'Contacts', bg: '#fef3c7', color: '#92400e' },
                      { icon: 'fa-heart-pulse', count: userDetail.vitals_count || 0, label: 'Vitals', bg: '#dcfce7', color: '#16a34a' },
                      { icon: 'fa-file-lines', count: userDetail.documents_count || 0, label: 'Docs', bg: '#fce7f3', color: '#be185d' }
                    ].map(b => (
                      <span key={b.label} style={{ padding: '4px 10px', borderRadius: '6px', background: b.bg, color: b.color, fontSize: '0.8rem' }}>
                        <i className={`fa-solid ${b.icon}`}></i> {b.count} {b.label}
                      </span>
                    ))}
                  </div>
                  {userDetail.recent_checkins?.length > 0 && (
                    <div style={{ marginTop: '0.5rem' }}>
                      <strong>Recent Check-ins:</strong>
                      <div style={{ maxHeight: '180px', overflowY: 'auto', marginTop: '0.5rem' }}>
                        {userDetail.recent_checkins.map((c, i) => (
                          <div key={i} style={{ padding: '0.4rem 0.5rem', borderRadius: '6px', background: '#f8f9fa', marginBottom: '0.25rem', fontSize: '0.8rem' }}>
                            <span style={{ color: '#2c5364', fontWeight: '600' }}>{formatDate(c.timestamp)}</span>
                            {c.feeling && <span style={{ marginLeft: '0.5rem', color: '#6b8899' }}>{c.feeling}</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Reset Password */}
                  <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: '#fef9ee', borderRadius: '8px', border: '1px solid #fde68a' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: resetPwUserId === userDetail.user.id ? '0.5rem' : 0 }}>
                      <strong style={{ fontSize: '0.85rem', color: '#92400e' }}><i className="fa-solid fa-key" style={{ marginRight: '6px' }}></i>Reset Password</strong>
                      {resetPwUserId !== userDetail.user.id && (
                        <button onClick={() => { setResetPwUserId(userDetail.user.id); setResetPwValue(''); }}
                          style={{ background: '#f59e0b', border: 'none', borderRadius: '6px', padding: '0.3rem 0.6rem', color: '#fff', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '600' }}
                          data-testid="admin-reset-pw-btn">
                          Change Password
                        </button>
                      )}
                    </div>
                    {resetPwUserId === userDetail.user.id && (
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <input type="text" value={resetPwValue} onChange={e => setResetPwValue(e.target.value)}
                          placeholder="New password (min 6 chars)"
                          style={{ flex: 1, padding: '0.4rem 0.6rem', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '0.85rem' }}
                          data-testid="admin-new-password-input" />
                        <button onClick={() => handleResetPassword(userDetail.user.id)}
                          className="submit-button" style={{ width: 'auto', padding: '0.4rem 0.75rem', fontSize: '0.8rem' }}
                          data-testid="admin-confirm-reset-pw-btn">
                          Reset
                        </button>
                        <button onClick={() => { setResetPwUserId(null); setResetPwValue(''); }}
                          style={{ padding: '0.4rem 0.6rem', borderRadius: '6px', border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', color: '#6b8899', fontSize: '0.8rem' }}>
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ===== ALERTS TAB ===== */}
        {activeTab === 'alerts' && (
          <div className="section-card">
            <h3 style={{ color: '#2c5364', marginBottom: '1rem' }}><i className="fa-solid fa-triangle-exclamation" style={{ color: '#ef4444', marginRight: '8px' }}></i>Active Alerts ({alerts.length})</h3>
            {alerts.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <div style={{ fontSize: '2.5rem', color: '#16a34a', marginBottom: '0.75rem' }}><i className="fa-solid fa-circle-check"></i></div>
                <h3 style={{ color: '#16a34a' }}>All Clear</h3>
                <p style={{ color: '#6b8899' }}>No overdue check-ins or active alerts</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {alerts.map((a, i) => (
                  <div key={i} style={{ padding: '1rem', borderRadius: '8px', borderLeft: `4px solid ${a.issues.some(is => is.type === 'red_alert') ? '#ef4444' : '#f59e0b'}`, background: a.issues.some(is => is.type === 'red_alert') ? '#fef2f2' : '#fefce8' }} data-testid={`alert-${a.user_id}`}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
                      <div>
                        <strong style={{ color: '#2c5364' }}>{a.name}</strong>
                        <div style={{ fontSize: '0.85rem', color: '#6b8899' }}>{a.email} {a.phone && `| ${a.phone}`}</div>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        {a.issues.map((issue, j) => (
                          <span key={j} style={{ padding: '4px 10px', borderRadius: '6px', fontSize: '0.8rem', fontWeight: '600', background: issue.type === 'red_alert' ? '#fee2e2' : '#fef3c7', color: issue.type === 'red_alert' ? '#dc2626' : '#92400e' }}>
                            {issue.type === 'overdue' ? `${issue.hours}h overdue` : 'RED ALERT'}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: '#6b8899' }}>Last: {formatDate(a.last_checkin)} | Due: {formatDate(a.next_checkin_due)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ===== LOGS TAB ===== */}
        {activeTab === 'logs' && (
          <div>
            <div className="section-card" style={{ marginBottom: '1.5rem' }} data-testid="create-log-section">
              <h3 style={{ color: '#2c5364', marginBottom: '1rem' }}><i className="fa-solid fa-pen-to-square" style={{ marginRight: '8px' }}></i>New Log Entry</h3>
              <form onSubmit={handleCreateLog} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <select value={newLog.note_type} onChange={e => setNewLog({ ...newLog, note_type: e.target.value })}
                    style={{ padding: '0.6rem', borderRadius: '8px', border: '2px solid #e2e8f0', minWidth: '140px' }} data-testid="log-type-select">
                    <option value="log">Log</option>
                    <option value="observation">Observation</option>
                    <option value="system">System Note</option>
                  </select>
                  <select value={newLog.target_user_id} onChange={e => setNewLog({ ...newLog, target_user_id: e.target.value })}
                    style={{ padding: '0.6rem', borderRadius: '8px', border: '2px solid #e2e8f0', flex: 1 }} data-testid="log-user-select">
                    <option value="">General (no specific user)</option>
                    {users.map(u => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
                  </select>
                </div>
                <textarea value={newLog.content} onChange={e => setNewLog({ ...newLog, content: e.target.value })}
                  placeholder="Enter log content..." rows={3} required
                  style={{ padding: '0.75rem', borderRadius: '8px', border: '2px solid #e2e8f0', resize: 'vertical' }} data-testid="log-content-input" />
                <button type="submit" className="submit-button" style={{ width: 'auto', alignSelf: 'flex-start' }} data-testid="create-log-btn">
                  <i className="fa-solid fa-plus"></i> Add Log Entry
                </button>
              </form>
            </div>

            <div className="section-card" data-testid="logs-list">
              <h3 style={{ color: '#2c5364', marginBottom: '1rem' }}><i className="fa-solid fa-clipboard-list" style={{ marginRight: '8px' }}></i>Activity Logs ({logs.length})</h3>
              {logs.length === 0 ? (
                <p style={{ color: '#94a3b8', textAlign: 'center', padding: '1rem' }}>No log entries yet</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {logs.map(log => {
                    const targetUser = users.find(u => u.id === log.target_user_id);
                    return (
                      <div key={log.id} style={{ padding: '0.75rem', borderRadius: '8px', background: '#f8f9fa', borderLeft: `4px solid ${log.note_type === 'observation' ? '#f59e0b' : log.note_type === 'system' ? '#3b82f6' : '#6b7280'}` }} data-testid={`log-${log.id}`}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.25rem' }}>
                              <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '0.7rem', fontWeight: '600', textTransform: 'uppercase', background: log.note_type === 'observation' ? '#fef3c7' : log.note_type === 'system' ? '#dbeafe' : '#f1f5f9', color: log.note_type === 'observation' ? '#92400e' : log.note_type === 'system' ? '#1e40af' : '#4b5563' }}>{log.note_type}</span>
                              {targetUser && <span style={{ fontSize: '0.8rem', color: '#2c5364', fontWeight: '600' }}>Re: {targetUser.name}</span>}
                              <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{formatDate(log.created_at)}</span>
                            </div>
                            <p style={{ margin: 0, color: '#374151', fontSize: '0.9rem' }}>{log.content}</p>
                          </div>
                          <button onClick={() => handleDeleteLog(log.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: '0.8rem', padding: '4px' }} data-testid={`delete-log-${log.id}`}>
                            <i className="fa-solid fa-trash"></i>
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ===== SETTINGS/CUSTOMIZE TAB ===== */}
        {activeTab === 'settings' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
            {/* Theme Color */}
            <div className="section-card" data-testid="theme-color-section">
              <h3 style={{ color: themeColor, marginBottom: '1rem' }}><i className="fa-solid fa-palette" style={{ marginRight: '8px' }}></i>Theme Color</h3>
              <p style={{ color: '#6b8899', fontSize: '0.9rem', marginBottom: '1rem' }}>Choose your dashboard accent color</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.75rem' }}>
                {THEME_COLORS.map(c => (
                  <button key={c.id} onClick={() => savePrefs({ theme_color: c.id })}
                    data-testid={`theme-${c.id}`}
                    style={{
                      padding: '0.75rem', borderRadius: '10px', border: prefs.theme_color === c.id ? '3px solid #000' : '2px solid #e2e8f0',
                      background: c.id, color: '#fff', cursor: 'pointer', fontWeight: '600', fontSize: '0.75rem',
                      transition: 'all 0.2s', transform: prefs.theme_color === c.id ? 'scale(1.05)' : 'scale(1)'
                    }}>
                    {prefs.theme_color === c.id && <i className="fa-solid fa-check" style={{ marginRight: '4px' }}></i>}
                    {c.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Wallpaper */}
            <div className="section-card" data-testid="wallpaper-section">
              <h3 style={{ color: themeColor, marginBottom: '1rem' }}><i className="fa-solid fa-image" style={{ marginRight: '8px' }}></i>Wallpaper</h3>
              <p style={{ color: '#6b8899', fontSize: '0.9rem', marginBottom: '1rem' }}>Set your dashboard background</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '0.75rem' }}>
                {WALLPAPERS.map(w => (
                  <button key={w.id} onClick={() => savePrefs({ wallpaper: w.id })}
                    data-testid={`wallpaper-${w.id}`}
                    style={{
                      height: '70px', borderRadius: '10px', cursor: 'pointer',
                      border: prefs.wallpaper === w.id ? '3px solid #000' : '2px solid #e2e8f0',
                      background: w.preview, color: w.id === 'none' ? '#6b8899' : '#fff',
                      fontWeight: '600', fontSize: '0.75rem', transition: 'all 0.2s',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transform: prefs.wallpaper === w.id ? 'scale(1.05)' : 'scale(1)'
                    }}>
                    {prefs.wallpaper === w.id && <i className="fa-solid fa-check" style={{ marginRight: '4px' }}></i>}
                    {w.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Preview */}
            <div className="section-card" style={{ gridColumn: '1 / -1' }} data-testid="preview-section">
              <h3 style={{ color: themeColor, marginBottom: '1rem' }}><i className="fa-solid fa-eye" style={{ marginRight: '8px' }}></i>Live Preview</h3>
              <div style={{
                height: '200px', borderRadius: '12px', overflow: 'hidden',
                background: prefs.wallpaper !== 'none' ? WALLPAPERS.find(w => w.id === prefs.wallpaper)?.preview : '#f0f4f8',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem', padding: '1.5rem'
              }}>
                <div style={{ background: 'rgba(255,255,255,0.9)', borderRadius: '12px', padding: '1.25rem', textAlign: 'center', minWidth: '120px', backdropFilter: 'blur(8px)' }}>
                  <div style={{ fontSize: '1.25rem', color: themeColor, marginBottom: '0.25rem' }}><i className="fa-solid fa-users"></i></div>
                  <div style={{ fontSize: '1.5rem', fontWeight: '700', color: themeColor }}>8</div>
                  <div style={{ fontSize: '0.7rem', color: '#6b8899' }}>Users</div>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.9)', borderRadius: '12px', padding: '1.25rem', textAlign: 'center', minWidth: '120px', backdropFilter: 'blur(8px)' }}>
                  <div style={{ fontSize: '1.25rem', color: '#16a34a', marginBottom: '0.25rem' }}><i className="fa-solid fa-circle-check"></i></div>
                  <div style={{ fontSize: '1.5rem', fontWeight: '700', color: themeColor }}>5</div>
                  <div style={{ fontSize: '0.7rem', color: '#6b8899' }}>OK</div>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.9)', borderRadius: '12px', padding: '1.25rem', textAlign: 'center', minWidth: '120px', backdropFilter: 'blur(8px)' }}>
                  <div style={{ fontSize: '1.25rem', color: '#ef4444', marginBottom: '0.25rem' }}><i className="fa-solid fa-clock"></i></div>
                  <div style={{ fontSize: '1.5rem', fontWeight: '700', color: themeColor }}>3</div>
                  <div style={{ fontSize: '0.7rem', color: '#6b8899' }}>Overdue</div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
